import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Table, Button, Badge, Form, InputGroup, Modal } from 'react-bootstrap';
import { FaSearch, FaFilter, FaEye, FaTrash, FaReply, FaEnvelope, FaCheckCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Header from '../../components/common/Header';
import ContactMessageService from '../../services/contactMessageService';

const ContactMessages = () => {
  const [messages, setMessages] = useState([]);
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({ unread: 0, read_count: 0, replied: 0, total: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);

  useEffect(() => {
    loadMessages();
  }, []);

  useEffect(() => {
    filterMessages();
  }, [messages, searchTerm, statusFilter]);

  const loadMessages = async () => {
    try {
      setLoading(true);
      const response = await ContactMessageService.getAllMessages();
      setMessages(response.data.messages || []);
      setCounts(response.data.counts || { unread: 0, read_count: 0, replied: 0, total: 0 });
    } catch (error) {
      toast.error('Failed to load messages');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const filterMessages = () => {
    let filtered = [...messages];

    if (searchTerm) {
      filtered = filtered.filter(m =>
        m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.subject.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(m => m.status === statusFilter);
    }

    setFilteredMessages(filtered);
  };

  const handleViewMessage = async (message) => {
    setSelectedMessage(message);
    setShowViewModal(true);
    
    if (message.status === 'unread') {
      try {
        await ContactMessageService.markAsRead(message.id);
        await loadMessages();
        // Update selected message status
        setSelectedMessage({ ...message, status: 'read' });
      } catch (error) {
        console.error('Mark as read error:', error);
      }
    }
  };

  const handleMarkAsReplied = async (id) => {
    try {
      const result = await ContactMessageService.markAsReplied(id);
      if (result.success) {
        toast.success('Message marked as replied');
        await loadMessages();
        if (selectedMessage && selectedMessage.id === id) {
          setSelectedMessage({ ...selectedMessage, status: 'replied' });
        }
      } else {
        toast.error(result.message || 'Failed to mark as replied');
      }
    } catch (error) {
      console.error('Mark as replied error:', error);
      toast.error('Failed to mark as replied');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      try {
        await ContactMessageService.deleteMessage(id);
        toast.success('Message deleted successfully');
        loadMessages();
        setShowViewModal(false);
      } catch (error) {
        toast.error('Failed to delete message');
      }
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'unread':
        return <Badge bg="danger">Unread</Badge>;
      case 'read':
        return <Badge bg="warning">Read</Badge>;
      case 'replied':
        return <Badge bg="success">Replied</Badge>;
      default:
        return <Badge bg="secondary">Unknown</Badge>;
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'unread':
        return <FaEnvelope className="text-danger" />;
      case 'read':
        return <FaCheckCircle className="text-warning" />;
      case 'replied':
        return <FaReply className="text-success" />;
      default:
        return null;
    }
  };

  return (
    <div>
      <Header />
      <div className="container-fluid p-4">
        <Row className="mb-4">
          <Col>
            <h2>
              <FaEnvelope className="me-2 text-primary" />
              Contact Messages
            </h2>
            <p className="text-muted">Messages sent from the website contact form</p>
          </Col>
        </Row>

        <Row className="mb-4">
          <Col md={3}>
            <Card className="shadow-sm border-0 bg-danger text-white">
              <Card.Body>
                <h6>Unread</h6>
                <h3>{counts.unread}</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="shadow-sm border-0 bg-warning text-white">
              <Card.Body>
                <h6>Read</h6>
                <h3>{counts.read_count}</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="shadow-sm border-0 bg-success text-white">
              <Card.Body>
                <h6>Replied</h6>
                <h3>{counts.replied}</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="shadow-sm border-0 bg-info text-white">
              <Card.Body>
                <h6>Total</h6>
                <h3>{counts.total}</h3>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card className="mb-4 shadow-sm">
          <Card.Body>
            <Row>
              <Col md={6}>
                <InputGroup>
                  <InputGroup.Text>
                    <FaSearch />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Search by name, email or subject..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col md={6}>
                <InputGroup>
                  <InputGroup.Text>
                    <FaFilter />
                  </InputGroup.Text>
                  <Form.Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="all">All Status</option>
                    <option value="unread">Unread</option>
                    <option value="read">Read</option>
                    <option value="replied">Replied</option>
                  </Form.Select>
                </InputGroup>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="shadow-sm">
          <Card.Body>
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <Table striped hover responsive>
                <thead className="bg-light">
                  <tr>
                    <th>#</th>
                    <th>Name/Contact</th>
                    <th>Subject</th>
                    <th>Message</th>
                    <th>Status</th>
                    <th>Received</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMessages.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center py-4">
                        No messages found
                      </td>
                    </tr>
                  ) : (
                    filteredMessages.map((message, index) => (
                      <tr key={message.id} className={message.status === 'unread' ? 'fw-bold' : ''}>
                        <td>{index + 1}</td>
                        <td>
                          <strong>{message.name}</strong>
                          <br />
                          <small className="text-muted">
                            {message.email}
                            {message.phone && ` | ${message.phone}`}
                          </small>
                        </td>
                        <td>{message.subject}</td>
                        <td>
                          <span title={message.message}>
                            {message.message.substring(0, 50)}...
                          </span>
                        </td>
                        <td>{getStatusBadge(message.status)}</td>
                        <td>
                          <small>{new Date(message.created_at).toLocaleString()}</small>
                        </td>
                        <td>
                          <Button
                            variant="info"
                            size="sm"
                            className="me-1"
                            onClick={() => handleViewMessage(message)}
                            title="View Message"
                          >
                            <FaEye />
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleDelete(message.id)}
                            title="Delete"
                          >
                            <FaTrash />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>

        {/* View Message Modal */}
        <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              {getStatusIcon(selectedMessage?.status)} Message from {selectedMessage?.name}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedMessage && (
              <>
                <Row className="mb-3">
                  <Col md={6}>
                    <strong>Name:</strong>
                    <p>{selectedMessage.name}</p>
                  </Col>
                  <Col md={6}>
                    <strong>Email:</strong>
                    <p><a href={`mailto:${selectedMessage.email}`}>{selectedMessage.email}</a></p>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={6}>
                    <strong>Phone:</strong>
                    <p>{selectedMessage.phone || 'Not provided'}</p>
                  </Col>
                  <Col md={6}>
                    <strong>Received:</strong>
                    <p>{new Date(selectedMessage.created_at).toLocaleString()}</p>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={12}>
                    <strong>Subject:</strong>
                    <p className="text-primary">{selectedMessage.subject}</p>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={12}>
                    <strong>Message:</strong>
                    <div className="border p-3 rounded bg-light">
                      {selectedMessage.message}
                    </div>
                  </Col>
                </Row>
                {selectedMessage.ip_address && (
                  <Row className="mb-3">
                    <Col md={12}>
                      <small className="text-muted">
                        IP Address: {selectedMessage.ip_address}
                      </small>
                    </Col>
                  </Row>
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            {selectedMessage && selectedMessage.status !== 'replied' && (
              <Button
                variant="success"
                onClick={() => handleMarkAsReplied(selectedMessage.id)}
              >
                <FaReply className="me-2" /> Mark as Replied
              </Button>
            )}
            <Button variant="secondary" onClick={() => setShowViewModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default ContactMessages;